// import Debug from "debug";
import * as React from 'react';
import { Spinner, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import {
  Field,
  Entity,
  getFieldArray,
  getFieldColumnAlign,
  getListRowLink,
  getRowKey,
  isFieldSortable,
  QueryOptions,
  Row,
  Sort,
  SortDirection,
  getFieldColumnStyle,
} from 'gossamer-unified';
import { ErrorBoundary } from '../ancillary/ErrorBoundary';
import { SubSectionMultiProps } from '../data/QueryMulti';
import { sortHeaderClicked } from '../data/Utils';
import { General } from '../fields/General';
import styles from './List.module.css';
import { GCacheQuerySectionProps } from '../data/useCacheQuery';
import { useDispatch } from 'react-redux';
import { convertKeysToPaths, setInfo } from '../data/ReduxLists';

// const debug = Debug("gfe/se/List");

export interface SortIconsProps {
  fieldId: string;
  sortHeaderClicked?: (fieldId: string, dir: SortDirection) => void;
  sorts?: Sort[];
}

export const SortIcons = (props: SortIconsProps): React.JSX.Element => {
  const getExistingSort = (fieldId: string) => {
    const sort: Sort | undefined = props.sorts?.find((sort: Sort) => sort[0] === fieldId);
    if (sort) {
      return [props.sorts?.indexOf(sort), sort[1]];
    }
    return null;
  };

  const getHeaderClassName = (fieldId: string, dir: SortDirection) => {
    let out = 'ControlIcon';
    const sort = getExistingSort(fieldId);
    if (sort && sort[1] === dir) {
      out += ` sort_${sort[0]}`;
    }
    return out;
  };

  return (
    <div className={styles.SortIcons}>
      <button
        onClick={props.sortHeaderClicked?.bind(null, props.fieldId, 'ASC')}
        className={getHeaderClassName(props.fieldId, 'ASC')}
        title="sort ascending by this column"
        type="button"
      >
        ▲
      </button>
      <button
        onClick={props.sortHeaderClicked?.bind(null, props.fieldId, 'DESC')}
        className={getHeaderClassName(props.fieldId, 'DESC')}
        title="sort descending by this column"
        type="button"
      >
        ▼
      </button>
    </div>
  );
};

export interface ListColumnHeaderProps {
  entity: Entity<any>;
  field: Field;
  fieldId: string;
  isSortable?: boolean;
  queryOptions?: QueryOptions;
  updateQueryOptions?: (newQueryOptions: QueryOptions) => void;
}

export const ListColumnHeader = (props: ListColumnHeaderProps): React.JSX.Element => {
  const sortable =
    props.isSortable !== false &&
    props.entity.sortable !== false &&
    isFieldSortable(props.field) &&
    props.queryOptions &&
    !!props.updateQueryOptions;
  const style: any = {};
  if (props.field.listColumnWidth!) {
    style.minWidth = props.field.listColumnWidth;
  }
  const className = `cellAlign-${getFieldColumnAlign(props.field)} colSortable-${sortable}`;
  return (
    <th key={props.fieldId}>
      <div style={style} className={className}>
        <span>{props.field.label}</span>
        {props.field.description && <a title={props.field.description}>&nbsp;ⓘ</a>}
        {sortable && (
          <SortIcons
            fieldId={props.fieldId}
            sortHeaderClicked={(fieldId: string, dir: SortDirection) =>
              props.updateQueryOptions?.(sortHeaderClicked(props.queryOptions, fieldId, dir))
            }
            sorts={props.queryOptions?.order}
          />
        )}
      </div>
    </th>
  );
};

export interface ListCellProps {
  field: Field;
  fieldId: string;
  isRowLinks: boolean;
  row: Row;
}

export const ListCell = (props: ListCellProps): React.JSX.Element => {
  try {
    return (
      <td
        key={props.fieldId}
        style={getFieldColumnStyle(props.field, props.row)}
        className={`cellAlign-${getFieldColumnAlign(props.field)}`}
      >
        <ErrorBoundary>
          <General
            editable={false}
            fieldId={props.fieldId}
            field={props.field}
            suppressLink={props.isRowLinks}
            value={(props.row as Record<string, unknown>)[props.fieldId] as any}
            row={props.row}
          />
        </ErrorBoundary>
      </td>
    );
  } catch (e) {
    console.error(e);
    return <td></td>;
  }
};

interface ListProps<T extends Row> extends SubSectionMultiProps<T> {}

export const List = <T extends Row>(props: ListProps<T>): React.JSX.Element | null => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isRowLinks = !!props.entity.listRowLink || !!props.onRowClick;
  const handleRowLinkClick = (key: string) => {
    if (props.onRowClick) {
      props.onRowClick(key);
    } else if (props.entity.listRowLink) {
      if (props.rowClickSetsSearchPaths) {
        console.log('calling updateListManagerWithRows', props.entity, props.rows);
        dispatch(
          setInfo({
            entityId: props.entity.id,
            info: {
              paths: convertKeysToPaths(props.entity, props.rows),
              originPagePath: window.location.pathname,
              originPageSearch: window.location.search,
            },
          }),
        );
      }
      const link = getListRowLink(props.entity.listRowLink, key);
      if (link.indexOf('/admin') === 0) {
        navigate(link);
      } else {
        // breakout
        window.location.href = link;
      }
    }
  };

  const columns = getFieldArray(props.entity).filter(([field, _]: [Field, string]) => field.listColumn);
  if (columns.length === 0) {
    return <div className="mb20px">No columns selected for display</div>;
  }

  const children = props.rows
    .filter((row) => !props.excludeValues || props.excludeValues.indexOf(getRowKey(props.entity, row)) === -1)
    .map((row: any, index: number) => {
      const key = getRowKey(props.entity, row);
      const className = isRowLinks ? styles.RowLink : undefined;
      return (
        <tr key={key} onClick={() => handleRowLinkClick(key)} className={className}>
          {columns.map(([field, fieldId]: [Field, string]) => (
            <ListCell key={fieldId} field={field} fieldId={fieldId} isRowLinks={isRowLinks} row={row} />
          ))}
        </tr>
      );
    });

  if (props.rows.length === 0) {
    return null;
  }
  return (
    <ErrorBoundary>
      <Table striped bordered responsive hover={isRowLinks}>
        <thead>
          <tr>
            {columns.map(([field, fieldId]: [Field, string]) => (
              <ListColumnHeader
                key={fieldId}
                entity={props.entity}
                field={field}
                fieldId={fieldId}
                queryOptions={props.queryOptions}
                updateQueryOptions={props.updateQueryOptions}
              />
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </Table>
    </ErrorBoundary>
  );
};

export const ListNew = <T extends Row>(props: GCacheQuerySectionProps<T>): React.JSX.Element => {
  const SubSection = props.render || List;
  const mainClassName = props.gCacheQuery.isFetching ? (styles as any).FetchingOpacity : '';
  return (
    <ErrorBoundary>
      {props.gCacheQuery.error && <div>{props.gCacheQuery.error}</div>}
      {props.gCacheQuery.isFetching && (
        <div>
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {props.gCacheQuery.data && !props.gCacheQuery.error && (
        <div className={mainClassName}>
          <SubSection
            entity={props.entity}
            queryOptions={props.queryOptions}
            rows={props.gCacheQuery.data}
            rowClickSetsSearchPaths={true}
            onRowClick={props.onRowClick}
            updateQueryOptions={props.setQueryOptions}
          />
        </div>
      )}
    </ErrorBoundary>
  );
};
